import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import EventCard from "@/components/feature/EventCard";
import { useSEO } from "@/hooks/useSEO";
import { useHashScroll } from "@/hooks/useHashScroll";
import {
  categoryList,
  countByCategory,
  getCategoryBySlug,
  getCategoryPath,
  getEventsByCategory,
  sortByHeat,
} from "@/utils/content";

export default function CategoryDetail() {
  useHashScroll();
  const { slug = "" } = useParams();
  const category = getCategoryBySlug(slug);
  const list = useMemo(() => sortByHeat(getEventsByCategory(slug)), [slug]);

  const jsonLd = useMemo(() => {
    if (!category) return undefined;
    return [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${category.name}事件梳理`,
        description: category.desc,
        inLanguage: "zh-CN",
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: list.length,
          itemListElement: list.map((event, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: event.title,
            url: `https://hot-melon-watch.example.com/event/${event.slug}`,
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: "https://hot-melon-watch.example.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: category.name,
            item: `https://hot-melon-watch.example.com/category/${category.slug}`,
          },
        ],
      },
    ];
  }, [category, list]);

  useSEO({
    title: category
      ? `${category.name}事件梳理 | 时间线复盘 - 热搜吃瓜`
      : "分类不存在 - 热搜吃瓜",
    description: category
      ? `${category.desc}当前收录 ${list.length} 个事件，每个都完整整理前因、经过与结果。`
      : "没有找到该分类，可返回瓜田总览查看全部事件。",
    keywords: category
      ? `热搜吃瓜,${category.name},热搜吃瓜事件梳理,热搜吃瓜时间线`
      : "热搜吃瓜",
    path: category ? `/category/${category.slug}` : "/events",
    jsonLd,
  });

  if (!category) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-content flex-1 flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-14 w-14 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-emotion-sad-line text-2xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            没有找到这个分类
          </h1>
          <Link
            to="/events"
            className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回瓜田总览
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-12">
            <Breadcrumbs items={[{ label: category.name }]} />
            <div className="mt-6 flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
              <div className="flex items-start gap-4">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-secondary-500 text-background-50">
                  <i className={`${category.icon} text-2xl`}></i>
                </span>
                <div>
                  <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                    {category.name} · 事件梳理
                  </h1>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                    {category.desc}
                  </p>
                </div>
              </div>
              <span className="flex w-fit items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-700">
                <i className="ri-stack-line text-base text-primary-600"></i>
                已收录 {list.length} 个事件
              </span>
            </div>

            <div className="mt-7 flex flex-wrap gap-2 border-t border-background-200 pt-5">
              {categoryList.map((item) => (
                <Link
                  key={item.slug}
                  to={getCategoryPath(item.slug)}
                  className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                    item.slug === category.slug
                      ? "bg-primary-500 font-medium text-background-50"
                      : "bg-background-50 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  <i className={item.icon}></i>
                  {item.name}
                  <span
                    className={`rounded-md px-1.5 text-xs ${
                      item.slug === category.slug
                        ? "bg-primary-600 text-background-50"
                        : "bg-background-200 text-foreground-600"
                    }`}
                  >
                    {countByCategory(item.slug)}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
          {list.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((event, index) => (
                <EventCard key={event.id} event={event} index={index} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center rounded-lg border border-background-200 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-md bg-background-200 text-foreground-500">
                <i className="ri-inbox-line text-2xl"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                这个分类还没有收录事件
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                我们会持续补充，先去其他分类看看已经理好的事件吧。
              </p>
              <Link
                to="/events"
                className="mt-5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                查看全部事件
              </Link>
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}